import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useConsumer } from '../../contexts/ConsumerContext';
import { useMapContext } from '../pages/Map/MapPageContext';
import { useParcelLoader } from '../hooks/useParcelLoader';
import { useConsumerT } from '../translations/landingConsumer';
import AlertSnackbar from '../components/AlertSnackbar';
import ConsumerBackButton from '../components/ConsumerBackButton';
import BottomSheet from '../components/BottomSheet';
import RelayPointCard from '../components/RelayPointCard';
//import { getRelayPoints } from '../../services/clientHandler';
import fallbackPoints from '../../assets/data/mondialrelayv5.json';
//import { useUser } from '../../contexts/UserContext';
// Fix bug icône Leaflet + Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function MapController({ mapRef }) {
  const map = useMap();
  mapRef.current = map;
  return null;
}

function normalize(point) {
  if (point.codePostal) return point;
  const cp = (point.ville || '').substring(0, 5);
  const city = (point.ville || '').substring(5).trim();
  return { ...point, codePostal: cp, ville: city };
}

const CENTER_FR = [46.603354, 1.888334];

export default function MapScreenContainer() {
  const t = useConsumerT();
  const navigate = useNavigate();
  const location = useLocation();
  const isSendBackFlow = location.pathname.includes('/sendback/map');
  const { dispatch: consumerDispatch } = useConsumer();
  const { fetchAlert, alertMessage, dispatch: pageDispatch } = useMapContext();
  useParcelLoader();

  const [allPoints, setAllPoints] = useState([]);
  const [filteredPoints, setFilteredPoints] = useState([]);
  const [filterQuery, setFilterQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetState, setSheetState] = useState('list');
  const markerRefs = useRef({});
  const cardRefs = useRef({});
  const scrollAreaRef = useRef(null);
  const mapRef = useRef(null);

  // without auth we couldn't get a jwt token, relay point are fetched base on a static file instead
  const loadPoints = useCallback(async () => {
    setLoading(true);
    //const response = await getRelayPoints(clientId, jwt);
    //if (response.status === 200 && response.results?.length > 0) {
    //  const pts = response.results.map(normalize);
    //  setAllPoints(pts);
    //  setFilteredPoints(pts);
    //} else {
      const pts = fallbackPoints.map(normalize);
      setAllPoints(pts);
      setFilteredPoints(pts);
      //if (response.status === 500) {
      //  pageDispatch({ type: 'PAGE_MAP_STATES_UPDATE', payload: { fetchAlert: true, alertMessage: t('map.error-fetch') } });
      //}
    //}
    setLoading(false);
  }, []);

  useEffect(() => { loadPoints(); }, [loadPoints]);

  const handleFilter = useCallback(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) {
      setFilteredPoints(allPoints);
      setSelected(null);
      mapRef.current?.setView(CENTER_FR, 6, { animate: true });
      return;
    }
    const filtered = allPoints.filter(p =>
      (p.ville || '').toLowerCase().includes(q) ||
      (p.codePostal || '').includes(q) ||
      (p.nom || '').toLowerCase().includes(q)
    );
    setFilteredPoints(filtered);
    setSelected(null);
    if (filtered.length > 0) {
      mapRef.current?.setView([parseFloat(filtered[0].lat), parseFloat(filtered[0].lng)], 12, { animate: true });
    }
  }, [filterQuery, allPoints]);

  function handleCardClick(point) {
    setSelected(point);
    const lat = parseFloat(point.lat);
    const lng = parseFloat(point.lng);
    // Appel impératif direct — pas de délai de re-render
    mapRef.current?.setView([lat, lng], 15, { animate: true });
    
    // Ouvrir le popup du marqueur sélectionné
    const marker = markerRefs.current[point.identifiant];
    if (marker) {
      marker.openPopup();
    }
    
    setTimeout(() => {
      const card = cardRefs.current[point.identifiant];
      const area = scrollAreaRef.current;
      if (card && area) {
        const cardTop = card.getBoundingClientRect().top;
        const areaTop = area.getBoundingClientRect().top;
        area.scrollTop += cardTop - areaTop - 8;
      }
    }, 300);
  }

  function handleSelectPoint(point) {
    consumerDispatch({ type: 'CONSUMER_POINT_SELECTED', point });
    if (isSendBackFlow) {
      navigate('/welldone', { state: { flow: 'sendback' } });
    }
  }

  return (
    <Page>
      <AlertSnackbar
        open={fetchAlert}
        message={alertMessage}
        onClose={() => pageDispatch({ type: 'PAGE_MAP_STATES_UPDATE', payload: { fetchAlert: false, alertMessage: '' } })}
      />

      <MapBg>
        {loading ? (
          <LoadingBox><CircularProgress size={28} sx={{ color: 'primary.main' }} /></LoadingBox>
        ) : (
          <MapContainer center={CENTER_FR} zoom={6} scrollWheelZoom zoomControl>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            <MapController mapRef={mapRef} />
            {filteredPoints.map(point => {
              const lat = parseFloat(point.lat);
              const lng = parseFloat(point.lng);
              if (!lat || !lng || isNaN(lat) || isNaN(lng)) return null;
              return (
                <Marker
                  key={point.identifiant}
                  position={[lat, lng]}
                  ref={ref => { if (ref) markerRefs.current[point.identifiant] = ref; }}
                  eventHandlers={{ click: () => handleCardClick(point) }}
                  opacity={selected?.identifiant === point.identifiant ? 1 : 0.65}
                >
                  <Popup autoPan={false}>
                    <PopupName>{point.nom}</PopupName>
                    <PopupAddr>{point.codePostal} {point.ville}</PopupAddr>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </MapBg>

      <TopFloating>
        <ConsumerBackButton onBack={() => navigate(-1)} variant="light" ariaLabel={t('common.back')} />
      </TopFloating>

      <BottomSheet variant="swipeable" state={sheetState} onStateChange={setSheetState} ariaLabel="Liste des points relais">
        {/* Hors du scroll container — toujours visible */}
        <SearchRow>
          <SearchInput
            type="search"
            placeholder={t('map.search-placeholder')}
            value={filterQuery}
            onChange={e => setFilterQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleFilter()}
          />
          <SearchBtn type="button" onClick={handleFilter} aria-label="Rechercher">
            <SearchIcon />
          </SearchBtn>
        </SearchRow>

        {!loading && filteredPoints.length > 0 && (
          <CountLine>{t('map.points-count', { count: filteredPoints.length })}</CountLine>
        )}

        {/* Scroll container — uniquement les cards */}
        <ScrollArea ref={scrollAreaRef}>
          {loading && <CenteredState><CircularProgress size={28} sx={{ color: 'primary.main' }} /></CenteredState>}

          {!loading && filteredPoints.length === 0 && (
            <EmptyState>
              <EmptyTitle>{t('map.empty-title')}</EmptyTitle>
              <EmptyBody>{t('map.empty-body')}</EmptyBody>
            </EmptyState>
          )}

          {!loading && filteredPoints.length > 0 && (
            <CardList>
              {filteredPoints.map(p => (
                <div key={p.identifiant} ref={el => { if (el) cardRefs.current[p.identifiant] = el; }}>
                  <RelayPointCard
                    point={p}
                    selected={selected?.identifiant === p.identifiant}
                    onClick={handleCardClick}
                    onSelect={isSendBackFlow ? handleSelectPoint : undefined}
                  />
                </div>
              ))}
            </CardList>
          )}
        </ScrollArea>
      </BottomSheet>
    </Page>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
    </svg>
  );
}

const Page = styled.div`
  height: 100vh;
  font-family: ${p => p.theme.typography.fontFamily};
  position: relative;
  overflow: hidden;
  background: ${p => p.theme.palette.background.default};
`;

/* z-index: 0 crée un stacking context qui confine les z-index internes de Leaflet
   (zoom controls à z-index 1000) en dessous du BottomSheet (z-index: 30) */
const MapBg = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  & .leaflet-container {
    height: 100%;
    width: 100%;
  }
`;

const LoadingBox = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TopFloating = styled.div`
  position: absolute;
  top: 8px;
  left: 16px;
  z-index: 10;
`;

/* Seul scroll container : la barre de recherche est AU-DESSUS, hors de ce div */
const ScrollArea = styled.div`
  overflow-y: auto;
  max-height: 46vh;
  padding-bottom: 8px;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid ${p => p.theme.palette.border.main};
  background: ${p => p.theme.palette.background.default};
  font-family: ${p => p.theme.typography.fontFamily};
  font-size: 14px;
  color: ${p => p.theme.palette.text.primary};
  outline: none;
  &:focus { border-color: ${p => p.theme.palette.primary.main}; }
`;

const SearchBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: ${p => p.theme.palette.text.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { opacity: 0.85; }
`;

const CountLine = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.theme.palette.text.disabled};
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CenteredState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
`;

const EmptyState = styled.div`text-align: center; padding: 24px 12px;`;
const EmptyTitle = styled.div`font-size: 14px; font-weight: 700; color: ${p => p.theme.palette.text.primary}; margin-bottom: 4px;`;
const EmptyBody = styled.p`font-size: 12px; color: ${p => p.theme.palette.text.secondary}; margin: 0; line-height: 1.5;`;

const PopupName = styled.div`font-weight: 700; font-size: 13px; margin-bottom: 2px;`;
const PopupAddr = styled.div`font-size: 12px; color: #555;`;
